import React from "react";
import { toast } from "react-toastify";
import { Link, useSearchParams } from "react-router-dom";

import { Button, IconButton, Switch } from "@mui/material";
import { Add, InfoOutlined, ModeEditOutlineTwoTone } from "@mui/icons-material";

import { useAppMeta, useTablePagination } from "hooks";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import CustomTabs from "components/Shared/CustomTabs";
import TableFilter from "components/Shared/AppTable/TableFilter";
import HighlightedText from "components/Shared/HighlightedText";
import {
  useLazyGetBranchAssignmentListQuery,
  useUpdateBranchAssignmentStatusMutation,
} from "store/services/branchAssignmentApi";

const BranchAssignmentListScreen = () => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";

  const breadcrumbs = [
    {
      title: "Branch-Assignment",
    },
  ];

  const branchAssignmentListTabs = [
    { label: "Active", value: "Active" },
    { label: "Inactive", value: "Inactive" },
  ];

  const [updateBranchAssignmentStatus] =
    useUpdateBranchAssignmentStatusMutation();

  // status update
  const handleStatusChange = async (row) => {
    const newStatus = row.status === "Active" ? "Inactive" : "Active";

    const isConfirmed = window.confirm(
      `Are you sure you want to change the status to ${newStatus}?`
    );

    if (isConfirmed) {
      try {
        await updateBranchAssignmentStatus({
          id: row._id,
          status: newStatus,
        }).unwrap();
        toast.success(`Branch Assignment status updated to ${newStatus}`);
      } catch (error) {
        const errorMessage = error?.data?.message || "Something went wrong!";
        toast.error(errorMessage);
      }
    }
  };

  const columns = [
    {
      name: "User",
      selector: (row) => row?.user?.name,
      cell: (row) => (
        <Link
          title={row?.user}
          to={`/app/crm/branchAssignment/${row._id}/detail`}
          className="line-clamp-2 hover:text-blue-600 hover:underline dark:hover:text-blue-300"
        >
          {row?.user ? (
            <HighlightedText text={row?.user?.name} highlight={keyword} />
          ) : (
            "N/A"
          )}
        </Link>
      ),
      minWidth: "250px",
      sortable: true,
    },
    {
      name: "Assignment Type",
      cell: (row) => (
        <div className="flex">
          <div className="mr-2">
            {row?.hasCustomAssignment
              ? "Branch Assign"
              : row?.assignmentType === "Region"
              ? "Province"
              : row?.assignmentType}
          </div>
        </div>
      ),
      minWidth: "250px",
      sortable: true,
    },
    {
      name: "Actions",
      cell: (row) => (
        <>
          <Link to={`/app/crm/branchAssignment/${row._id}/detail`}>
            <IconButton
              size="small"
              aria-label="View Branch Assignment"
              color="primary"
            >
              <InfoOutlined />
            </IconButton>
          </Link>

          <>
            <Link
              to={`/app/crm/branchAssignment/${row._id}/edit`}
              state={{ from: "list" }}
            >
              <IconButton size="small" aria-label="Edit Assignment">
                <ModeEditOutlineTwoTone />
              </IconButton>
            </Link>

            <Switch
              checked={row.status === "Active"}
              onChange={() => handleStatusChange(row)}
              color="primary"
              inputProps={{ "aria-label": "Status Toggle" }}
            />
          </>
        </>
      ),
    },
  ];

  // RTK Query
  const [
    fetchBranchAssignments,
    {
      data: branchAssignmentRes,
      isLoading: loadingBranchAssignments,
      isFetching: fetchingBranchAssignments,
      error,
    },
  ] = useLazyGetBranchAssignmentListQuery();

  React.useEffect(() => {
    fetchBranchAssignments({
      status: activeTab,
      page: currentPage,
      limit: perPage,
      ...(keyword && { search: keyword }),
    });
  }, [activeTab, currentPage, fetchBranchAssignments, keyword, perPage]);

  useAppMeta(`${activeTab} BranchAssignment`);
  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      headerActions={activeTab === "Active" && <PageActions />}
    >
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        <CustomTabs tabs={branchAssignmentListTabs} activeTab={activeTab} />

        <TableFilter
          searchLabel="Name"
          disabled={loadingBranchAssignments || fetchingBranchAssignments}
        >
          <div className="font-medium"> Branch Assignments</div>
        </TableFilter>

        <AppTable
          columns={columns}
          data={branchAssignmentRes?.data ?? []}
          progressPending={
            loadingBranchAssignments || fetchingBranchAssignments
          }
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={branchAssignmentRes?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default BranchAssignmentListScreen;

const PageActions = () => {
  return (
    <Link to="/app/crm/branchAssignment/create">
      <Button startIcon={<Add />} variant="contained">
        New Branch Assignment
      </Button>
    </Link>
  );
};
